"use client";

import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { useBscDepositModalStore } from "@/stores/useBscDepositModalStore";

const BscDepositModal = dynamic(() => import("../BscDepositModal"), { ssr: false });
const HyperliquidWithdrawModal = dynamic(() => import("../HyperliquidWithdrawModal"), { ssr: false });
const PolymarketWithdrawModal = dynamic(() => import("../PolymarketWithdrawModal"), { ssr: false });
const HyperliquidEnableModal = dynamic(() => import("../HyperliquidEnableModal"), { ssr: false });

/**
 * Three of these modals (HL withdraw, PM withdraw, HL enable) follow
 * the same pattern: a local boolean opens on a window CustomEvent
 * dispatched from /settings or other deep entry points. The shared
 * helper below collapses the boilerplate. BSC deposit is already
 * driven by a Zustand store, so it stays as its own component.
 */
function useWindowOpen(...eventNames: string[]) {
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => setIsOpen(true);
    for (const name of eventNames) {
      window.addEventListener(name, handler);
    }
    return () => {
      for (const name of eventNames) {
        window.removeEventListener(name, handler);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [isOpen, () => setIsOpen(false)] as const;
}

export function BscDepositModalWrapper() {
  const { isOpen, close } = useBscDepositModalStore();
  return <BscDepositModal isOpen={isOpen} onClose={close} />;
}

/**
 * Single mount per app — listens for `frenflow:hl-withdraw-open`
 * (dispatched from /settings) and opens the Hyperliquid withdraw flow.
 */
export function HyperliquidWithdrawModalGlobal() {
  const [isOpen, close] = useWindowOpen("frenflow:hl-withdraw-open");
  return <HyperliquidWithdrawModal isOpen={isOpen} onClose={close} />;
}

/**
 * Mirror of HyperliquidWithdrawModalGlobal for Polymarket. Listens
 * for `frenflow:pm-withdraw-open`. Single mount per app.
 */
export function PolymarketWithdrawModalGlobal() {
  const [isOpen, close] = useWindowOpen("frenflow:pm-withdraw-open");
  return <PolymarketWithdrawModal isOpen={isOpen} onClose={close} />;
}

export function HyperliquidEnableModalGlobal() {
  const [isOpen, close] = useWindowOpen("frenflow:hl-deposit-open", "frenflow:hl-enable-open");
  return <HyperliquidEnableModal isOpen={isOpen} onClose={close} />;
}
